import { useEffect, useState, useRef, KeyboardEvent, useCallback } from "react";
import {
  useEditor,
  EditorContent,
  Editor as TiptapEditor,
} from "@tiptap/react";
import { markdownExtensions } from "../../editor/extensions/markdown";
import EditorToolbar from "./Toolbar";
import CommandMenu from "./CommandMenu";
import { useWorkspaceStore } from "../../store/workspace";
import { Bot } from "lucide-react";
import { Node } from "unist";
import { ChangeCollector } from "../../utils/ChangeCollector";

interface EditorProps {
  initialContent?: string;
  onUpdate?: (content: string) => void;
  documentId: string;
}

// 改进的防抖函数，可以被取消
function debounce<
  T extends (
    content: string,
    titleValue: string,
    ast: Node | null,
    docId: string
  ) => void
>(
  fn: T,
  delay: number
): {
  (content: string, titleValue: string, ast: Node | null, docId: string): void;
  cancel: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const debouncedFn = function (
    content: string,
    titleValue: string,
    ast: Node | null,
    docId: string
  ) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(content, titleValue, ast, docId), delay);
  };

  debouncedFn.cancel = function () {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return debouncedFn as typeof debouncedFn;
}

const Editor = ({ initialContent = "", onUpdate, documentId }: EditorProps) => {
  const [showAIToolbar] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const { updateDocument, documents } = useWorkspaceStore();
  const titleInputRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const [isUserEditing, setIsUserEditing] = useState(false);

  // 记录当前操作的文档ID，用于防止竞态条件
  const currentDocIdRef = useRef<string>(documentId);
  // 记录上一个文档ID，用于在文档切换时保存内容
  const prevDocIdRef = useRef<string>(documentId);

  // 添加一个标志，表示内容是否正在被程序更新（非用户编辑）
  const isProgrammaticUpdate = useRef(false);

  // 记录每个文档的内容哈希，避免重复更新
  const contentHashMapRef = useRef<Record<string, string>>({});

  // 初始化 ChangeCollector 实例
  const changeCollector = useRef<ChangeCollector>(
    new ChangeCollector()
  ).current;

  // 获取当前文档标题和内容
  const currentDocument = documentId ? documents[documentId] : null;
  const [title, setTitle] = useState(currentDocument?.title || "");
  const [localTitle, setLocalTitle] = useState(currentDocument?.title || "");

  // 保存每个文档ID对应的防抖函数，以便在切换文档时取消
  const debouncedUpdateMapRef = useRef<
    Record<string, ReturnType<typeof debounce>>
  >({});

  // 编辑器引用
  const editorInstanceRef = useRef<TiptapEditor | null>(null);

  // 追踪上次更新时间，用于防止循环更新
  const lastUpdateTimeRef = useRef<number>(0);

  // 辅助函数：获取编辑器当前的Markdown内容和AST
  const getMarkdownContent = useCallback(
    (editorInstance: TiptapEditor) => {
      // 获取 Markdown 内容
      let markdown = "";

      if (
        editorInstance.storage &&
        editorInstance.storage.markdown &&
        typeof editorInstance.storage.markdown.getMarkdown === "function"
      ) {
        markdown = editorInstance.storage.markdown.getMarkdown();
      } else {
        // 如果 markdown 扩展不可用，则使用 HTML
        markdown = editorInstance.getHTML();
      }

      // 使用 ChangeCollector 处理 Markdown 并生成 AST
      return changeCollector.processContent(markdown);
    },
    [changeCollector]
  );

  // 使用防抖更新函数，延迟 500ms
  const createDebouncedUpdate = useCallback(
    (docId: string) => {
      // 如果这个文档ID已经有防抖函数，直接返回
      if (debouncedUpdateMapRef.current[docId]) {
        return debouncedUpdateMapRef.current[docId];
      }

      // 创建新的防抖函数
      const debouncedFn = debounce(
        (
          content: string,
          titleValue: string,
          ast: Node | null,
          docId: string
        ) => {
          // 如果文档ID已经变了，放弃这次更新
          if (docId !== currentDocIdRef.current) {
            return;
          }

          // 如果是程序引起的更新，不执行保存
          if (isProgrammaticUpdate.current) {
            return;
          }

          // 计算内容哈希，用于避免相同内容的重复保存
          const contentHash = `${content.length}-${content.slice(
            0,
            50
          )}-${titleValue}`;

          // 检查该文档的哈希值是否变化
          if (contentHashMapRef.current[docId] === contentHash) {
            return;
          }

          // 更新该文档的哈希值
          contentHashMapRef.current[docId] = contentHash;

          // 执行正式的文档更新，通过store更新
          // console.log("执行防抖更新:", {
          //   docId,
          //   contentLength: content.length,
          //   title: titleValue,
          //   astPresent: !!ast,
          // });

          // 使用类型判断，如果 ast 为 null 则传递 undefined
          updateDocument(docId, content, titleValue, ast || undefined);
          onUpdate?.(content);

          // 设置一个短暂的保存标记，避免潜在的循环更新
          // 在数据从用户输入流向存储后，暂时阻止从存储流回编辑器
          const UPDATE_PROTECTION_WINDOW = 100; // 毫秒
          const currentTime = Date.now();
          lastUpdateTimeRef.current = currentTime;

          // 100ms后重置，允许真正需要的更新
          setTimeout(() => {
            if (lastUpdateTimeRef.current === currentTime) {
              lastUpdateTimeRef.current = 0;
            }
          }, UPDATE_PROTECTION_WINDOW);
        },
        500
      );

      // 缓存这个防抖函数
      debouncedUpdateMapRef.current[docId] = debouncedFn;

      return debouncedFn;
    },
    [updateDocument, onUpdate]
  );

  // 取消指定文档的防抖计时器
  const cancelDebounce = useCallback((docId: string) => {
    if (debouncedUpdateMapRef.current[docId]) {
      // console.log("取消文档防抖计时器:", docId);
      debouncedUpdateMapRef.current[docId].cancel();
    }
  }, []);

  // 立即保存文档内容（不使用防抖）
  const saveDocumentImmediately = useCallback(
    (docId: string, content: string, titleValue: string, ast: Node | null) => {
      if (!docId || !documents[docId]) return;

      // 先取消该文档的防抖计时器
      cancelDebounce(docId);

      // console.log("立即保存文档:", {
      //   docId,
      //   contentLength: content.length,
      //   title: titleValue,
      // });
      updateDocument(docId, content, titleValue, ast || undefined);
      onUpdate?.(content);

      // 更新文档哈希，避免重复保存
      const contentHash = `${content.length}-${content.slice(
        0,
        50
      )}-${titleValue}`;
      contentHashMapRef.current[docId] = contentHash;
    },
    [documents, onUpdate, updateDocument, cancelDebounce]
  );

  const editor = useEditor({
    extensions: markdownExtensions,
    content: initialContent,
    editorProps: {
      attributes: {
        class: "prose-invert w-full focus:outline-none",
        style: "height: 100%; min-height: 200px; max-width: none;",
      },
      handleKeyDown: (view, event) => {
        // 当按下向上键且光标在文档开头时，将焦点移到标题
        if (event.key === "ArrowUp" && view.state.selection.anchor === 0) {
          event.preventDefault();
          titleInputRef.current?.focus();

          // 将光标移到文本末尾
          if (titleInputRef.current) {
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(titleInputRef.current);
            range.collapse(false); // 将范围折叠到末尾
            sel?.removeAllRanges();
            sel?.addRange(range);
          }

          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor, transaction }) => {
      // 更新编辑器引用
      editorInstanceRef.current = editor;

      // 如果是程序更新内容，不触发保存
      if (isProgrammaticUpdate.current) {
        return;
      }

      // 检查事务元数据，如果是AI生成或标记为不保存，则跳过
      if (
        transaction?.getMeta("preventSave") ||
        transaction?.getMeta("aiGeneration")
      ) {
        return;
      }

      // 获取当前操作的文档ID
      const currentDocId = currentDocIdRef.current;

      try {
        // 获取Markdown内容和AST
        const { markdown: processedMarkdown, ast } = getMarkdownContent(editor);

        // 使用防抖更新，避免频繁保存
        const currentTitle = title.trim() || "未命名";
        // 获取当前文档的防抖函数
        const debouncedFn = createDebouncedUpdate(currentDocId);
        debouncedFn(processedMarkdown, currentTitle, ast, currentDocId);

        // console.log('编辑器内容已更新，触发防抖保存', { docId: currentDocId, astPresent: !!ast });
      } catch (error) {
        console.error("编辑器更新时出错:", error);
      }
    },
    onSelectionUpdate: ({ editor }) => {
      // 更新编辑器引用
      editorInstanceRef.current = editor;

      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, " ");
      if (text !== selectedText) {
        setSelectedText(text);
      }
    },
  });

  // 当编辑器实例创建后更新引用
  useEffect(() => {
    if (editor) {
      editorInstanceRef.current = editor;
    }
  }, [editor]);

  // 当文档ID变化时，保存前一个文档并更新引用
  useEffect(() => {
    // 如果文档ID变化了，并且编辑器存在
    if (documentId !== prevDocIdRef.current && editor && !editor.isDestroyed) {
      const prevDocId = prevDocIdRef.current;

      // 确保上一个文档存在且不是初始化状态
      if (prevDocId && documents[prevDocId] && prevDocId !== documentId) {
        try {
          // 先取消前一个文档的防抖
          cancelDebounce(prevDocId);

          // 获取Markdown内容和AST
          const { markdown: processedMarkdown, ast } =
            getMarkdownContent(editor);

          // 获取当前标题
          const currentTitle = title.trim() || "未命名";

          // 立即保存上一个文档的内容
          saveDocumentImmediately(
            prevDocId,
            processedMarkdown,
            currentTitle,
            ast
          );

          // console.log("文档切换，已保存上一个文档:", prevDocId);
        } catch (error) {
          console.error("保存上一个文档时出错:", error);
        }
      }

      // 取消当前文档的防抖，防止竞态条件
      cancelDebounce(documentId);
    }

    // 更新当前文档ID引用
    currentDocIdRef.current = documentId;
    prevDocIdRef.current = documentId;
  }, [
    documentId,
    documents,
    editor,
    saveDocumentImmediately,
    title,
    changeCollector,
    cancelDebounce,
    getMarkdownContent,
  ]);

  // 当文档ID变化时更新标题，但仅在用户不在编辑时
  useEffect(() => {
    if (documentId && documents[documentId]) {
      const docTitle = documents[documentId].title || "";
      setTitle(docTitle);
      setLocalTitle(docTitle);

      // 如果标题输入框有内容，需要手动更新
      if (titleInputRef.current && !isUserEditing) {
        titleInputRef.current.textContent = docTitle;
      }
    }
  }, [documentId, documents, isUserEditing]);

  // 处理标题变化 - 只更新本地状态，不更新文档
  const handleTitleChange = (e: React.FormEvent<HTMLDivElement>) => {
    const newTitle = e.currentTarget.textContent || "";
    setLocalTitle(newTitle);
  };

  // 处理标题输入框的键盘事件
  const handleTitleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault(); // 阻止默认行为

      // 提交标题更新
      updateDocumentTitle();

      // 将焦点移到编辑器开头
      editor?.commands.focus("start");
      return;
    }

    // 当按下向下键时，将焦点移到编辑器
    if (e.key === "ArrowDown") {
      e.preventDefault();
      editor?.commands.focus("start");
    }
  };

  // 处理标题输入框获取焦点
  const handleTitleFocus = () => {
    setIsUserEditing(true);
  };

  // 处理标题输入框失去焦点 - 此时更新文档标题
  const handleTitleBlur = () => {
    setIsUserEditing(false);
    updateDocumentTitle();
  };

  // 更新文档标题的函数
  const updateDocumentTitle = useCallback(() => {
    if (documentId) {
      const doc = documents[documentId];
      if (doc) {
        // 如果标题为空，则使用"未命名"
        const displayTitle = localTitle.trim() || "未命名";

        // 只有当标题真正变化时才更新
        if (displayTitle !== title) {
          setTitle(displayTitle);

          // 立即更新文档标题，不使用防抖
          // console.log("立即更新文档标题:", {
          //   docId: documentId,
          //   title: displayTitle,
          // });

          // 先取消该文档的防抖计时器
          cancelDebounce(documentId);

          // 立即更新文档，不使用防抖
          updateDocument(
            documentId,
            doc.content,
            displayTitle,
            doc.ast || undefined
          );
        }
      }
    }
  }, [
    documentId,
    documents,
    cancelDebounce,
    updateDocument,
    localTitle,
    title,
  ]);

  // 当文档ID变化时更新编辑器内容，并清除 ChangeCollector 缓存
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // 立即更新当前文档ID引用
    currentDocIdRef.current = documentId;

    if (documentId && documents[documentId]) {
      const content = documents[documentId].content || "";

      try {
        // 检查是否在短时间内刚执行过更新，如果是则可能是由于存储更新触发的重渲染
        // 此检查有助于防止用户输入->存储->重渲染->编辑器刷新的循环
        const currentTime = Date.now();
        if (currentTime - lastUpdateTimeRef.current < 150) {
          // console.log(
          //   "跳过编辑器刷新 - 刚刚执行过更新，可能是状态变化导致的重渲染"
          // );
          return;
        }

        // 取消任何可能挂起的防抖更新
        // 确保不会触发旧文档的更新
        isProgrammaticUpdate.current = true;

        // 初始化或更新文档哈希
        const newHash = `${content.length}-${content.slice(0, 50)}-${
          documents[documentId].title || ""
        }`;
        contentHashMapRef.current[documentId] = newHash;

        // 直接设置内容，不触发更新保存
        // 第二个参数设为false，避免导致Tiptap内部的emitUpdate回调
        editor.commands.setContent(content, false);

        // 清除缓存
        changeCollector.clearCache();

        // 检查文档是否是新创建的（内容为空且标题是默认的"新文档"）
        const isNewDocument =
          !content && documents[documentId].title === "新文档";

        // 重置标志并处理新文档
        setTimeout(() => {
          if (
            currentDocIdRef.current === documentId &&
            editor &&
            !editor.isDestroyed
          ) {
            // 对于新文档，不设置编辑器焦点，而是将焦点放在标题上并全选
            if (isNewDocument && titleInputRef.current) {
              // 聚焦标题输入框
              titleInputRef.current.focus();

              // 全选标题文本
              const range = document.createRange();
              const sel = window.getSelection();
              range.selectNodeContents(titleInputRef.current);
              sel?.removeAllRanges();
              sel?.addRange(range);
            }

            // 重置标志，允许用户编辑触发更新
            isProgrammaticUpdate.current = false;
          }
        }, 50); // 延长一点时间，确保DOM更新完成
      } catch (error) {
        console.error("更新编辑器内容时出错:", error);
        // 确保标志被重置
        isProgrammaticUpdate.current = false;
      }
    }
  }, [documentId, documents, editor, changeCollector]);

  // 在组件卸载时清理所有防抖计时器
  useEffect(() => {
    return () => {
      if (editor && !editor.isDestroyed) {
        editor.destroy();
      }

      // 取消所有文档的防抖计时器
      Object.keys(debouncedUpdateMapRef.current).forEach((docId) => {
        debouncedUpdateMapRef.current[docId].cancel();
      });

      // 重置标志和缓存
      isProgrammaticUpdate.current = false;
      contentHashMapRef.current = {};
      debouncedUpdateMapRef.current = {};
    };
  }, [editor]);

  if (!editor) {
    return null; // 或者显示加载状态
  }

  return (
    <div className="flex flex-col w-full mx-auto h-full">
      <EditorToolbar editor={editor} />

      {showAIToolbar && (
        <div className="p-2 bg-gray-800 border-b border-gray-700">
          <button
            className="px-2 py-1 bg-blue-600 text-white rounded text-sm flex items-center gap-1"
            onClick={() => {
              // 这里可以添加AI功能的实现
            }}
          >
            <Bot size={16} />
            <span>AI助手</span>
          </button>
        </div>
      )}

      {/* 编辑器内容容器 - 确保它可以滚动且有正确的高度 */}
      <div
        ref={editorRef}
        className="flex-1 overflow-auto w-full editor-content-container"
      >
        {/* 文档内容区 - 标题和编辑器内容 */}
        <div className="flex flex-col items-center w-full">
          {/* 文档标题编辑区域 */}
          <div className="document-title-container max-w-[800px]">
            <div
              ref={titleInputRef}
              contentEditable
              className="document-title-input"
              onInput={handleTitleChange}
              onKeyDown={handleTitleKeyDown}
              onFocus={handleTitleFocus}
              onBlur={handleTitleBlur}
              suppressContentEditableWarning={true}
              data-placeholder="未命名"
            >
              {title}
            </div>
          </div>

          <div
            onClick={() => editor.commands.focus()}
            className="max-w-[800px] w-full h-full"
          >
            <EditorContent editor={editor} className="editor-wrapper" />
          </div>
        </div>
      </div>

      <CommandMenu editor={editor} />
    </div>
  );
};

export default Editor;
